
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { EpisodeEditDialog } from "./episodes/EpisodeEditDialog";
import { EpisodeCard } from "./episodes/EpisodeCard";
import { useEpisodes } from "./episodes/useEpisodes";
import { EpisodeWithCategory } from "./episodes/types";
import { AdminSearch } from "./shared/AdminSearch";
import { AdminFilters } from "./shared/AdminFilters";
import { AdminPagination } from "./shared/AdminPagination";
import { usePagination } from "@/hooks/usePagination";

export const EpisodesManager = () => {
  const {
    episodes,
    categories,
    loading,
    saving,
    saveEpisode,
    deleteEpisode
  } = useEpisodes();
  
  const [editingEpisode, setEditingEpisode] = useState<EpisodeWithCategory | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    category: ""
  });

  // Filter and search episodes
  const filteredEpisodes = useMemo(() => {
    return episodes.filter(episode => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = episode.title.toLowerCase().includes(searchLower) || episode.description?.toLowerCase().includes(searchLower) || episode.episodes_categories?.name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && episode.status !== filters.status) {
        return false;
      }

      // Category filter
      if (filters.category && episode.category_id !== filters.category) {
        return false;
      }
      return true;
    });
  }, [episodes, searchTerm, filters]);

  // Pagination
  const pagination = usePagination({
    items: filteredEpisodes,
    itemsPerPage: 10
  });

  const handleSave = async (episodeData: any) => {
    await saveEpisode(episodeData, editingEpisode?.id);
    setEditingEpisode(null);
  };

  const handleEdit = (episode: EpisodeWithCategory) => {
    setEditingEpisode(episode);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingEpisode(null);
    setDialogOpen(true);
  };

  const handleDelete = async (episodeId: string) => {
    await deleteEpisode(episodeId);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    pagination.resetPagination();
  };

  const handleResetFilters = () => {
    setFilters({
      status: "",
      category: ""
    });
    setSearchTerm("");
    pagination.resetPagination();
  };

  if (loading) {
    return <div className="text-center">Loading episodes...</div>;
  }

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name
  }));

  const hasFilters = Boolean(searchTerm || Object.values(filters).some(v => v));

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-hidden">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Episodes Management</h2>
        <Button onClick={handleCreate} disabled={saving}>
          <Plus className="h-4 w-4 mr-2" />
          Add Episode
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <AdminSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search episodes..." className="max-w-md" />
        
        <AdminFilters filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} categoryOptions={categoryOptions} />
      </div>

      <EpisodeEditDialog
        episode={editingEpisode}
        categories={categories}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        saving={saving}
      />

      <div className="grid gap-4">
        {pagination.paginatedItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {hasFilters 
              ? "No episodes match your search criteria." 
              : "No episodes found. Click 'Add Episode' to create your first episode."
            }
          </div>
        ) : (
          pagination.paginatedItems.map(episode => (
            <EpisodeCard 
              key={episode.id} 
              episode={episode} 
              onEdit={handleEdit} 
              onDelete={handleDelete} 
            />
          ))
        )}
      </div>

      <AdminPagination 
        currentPage={pagination.currentPage} 
        totalPages={pagination.totalPages} 
        itemsPerPage={10} 
        totalItems={filteredEpisodes.length} 
        onPageChange={pagination.goToPage} 
        onItemsPerPageChange={() => {}} // Fixed items per page for now
      />
    </div>
  );
};
